import React, { useEffect, useState } from "react";
import {
  getAllLeavesApi,
  approveLeaveApi,
  rejectLeaveApi,
} from "../../Api/leaveApi";
import {
  Search,
  X,
  Calendar,
  Download,
  CheckCircle,
  XCircle,
  Eye,
  FileText,
} from "lucide-react";

const Leaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });

  const itemsPerPage = 10;

  useEffect(() => { loadLeaves(); }, []);

  const loadLeaves = async () => {
    try {
      const res = await getAllLeavesApi();
      if (res.success) { setLeaves(res.data); calculateStats(res.data); }
    } catch (error) { console.error("Error loading leaves:", error); }
    finally { setLoading(false); }
  };

  const calculateStats = (data) => {
    const pending  = data.filter((l) => l.status?.toLowerCase() === "pending").length;
    const approved = data.filter((l) => l.status?.toLowerCase() === "approved").length;
    const rejected = data.filter((l) => l.status?.toLowerCase() === "rejected").length;
    setStats({ pending, approved, rejected, total: data.length });
  };

  const approveLeave = async (id) => {
    try { await approveLeaveApi(id); loadLeaves(); if (selected?._id === id) setSelected((p) => ({ ...p, status: "approved" })); }
    catch (e) { console.error(e); }
  };

  const rejectLeave = async (id) => {
    try { await rejectLeaveApi(id); loadLeaves(); if (selected?._id === id) setSelected((p) => ({ ...p, status: "rejected" })); }
    catch (e) { console.error(e); }
  };

  const filteredLeaves = leaves.filter((leave) => {
    const matchesSearch =
      leave.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leave.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || leave.status?.toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const indexOfLast  = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentLeaves = filteredLeaves.slice(indexOfFirst, indexOfLast);
  const totalPages    = Math.ceil(filteredLeaves.length / itemsPerPage);

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase();
    if (s === "approved")
      return (
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
          <span className="px-3 py-1.5 bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 rounded-full text-xs font-semibold border border-emerald-200">Approved</span>
        </div>
      );
    if (s === "rejected")
      return (
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-red-400 rounded-full"></div>
          <span className="px-3 py-1.5 bg-gradient-to-r from-red-50 to-rose-50 text-red-700 rounded-full text-xs font-semibold border border-red-200">Rejected</span>
        </div>
      );
    return (
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 bg-violet-400 rounded-full animate-pulse"></div>
        <span className="px-3 py-1.5 bg-gradient-to-r from-violet-50 to-indigo-50 text-violet-700 rounded-full text-xs font-semibold border border-violet-200">Pending</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700;800&family=Space+Mono:wght@400;700&display=swap');
        body { font-family: 'Plus Jakarta Sans', sans-serif; }

        .gradient-card {
          background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(249,250,251,0.95) 100%);
          border: 1px solid rgba(99,102,241,0.1);
          backdrop-filter: blur(10px);
        }
        .gradient-text {
          background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .stat-card { position: relative; overflow: hidden; transition: all 0.3s ease; }
        .stat-card::before {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(6,182,212,0.05) 0%, rgba(99,102,241,0.05) 100%);
          border-radius: 16px;
        }
        .stat-card:hover { transform: translateY(-4px); box-shadow: 0 16px 32px rgba(99,102,241,0.12); }

        .fade-in { animation: fadeIn 0.6s ease-out forwards; opacity: 0; }
        .fade-in-delay-1 { animation-delay: 0.1s; }
        .fade-in-delay-2 { animation-delay: 0.2s; }
        .fade-in-delay-3 { animation-delay: 0.3s; }
        .fade-in-delay-4 { animation-delay: 0.4s; }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .table-row-hover { transition: all 0.3s ease; }
        .table-row-hover:hover {
          background: linear-gradient(90deg, rgba(99,102,241,0.06) 0%, transparent 100%);
          transform: translateX(4px);
        }
        .search-input { transition: all 0.3s ease; background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); }
        .search-input:focus { box-shadow: 0 0 20px rgba(99,102,241,0.15); background: white; }

        .modal-backdrop { animation: backdropFadeIn 0.3s ease-out; }
        @keyframes backdropFadeIn { from { opacity: 0; } to { opacity: 1; } }
        .modal-content { animation: modalSlideUp 0.4s ease-out; }
        @keyframes modalSlideUp {
          from { opacity: 0; transform: translateY(40px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .button-hover { transition: all 0.3s cubic-bezier(0.34,1.56,0.64,1); }
        .button-hover:hover { transform: translateY(-2px); box-shadow: 0 12px 24px rgba(6,182,212,0.25); }
        .header-gradient { background: linear-gradient(135deg, rgba(6,182,212,0.08) 0%, rgba(99,102,241,0.08) 100%); }

        .approve-btn { transition: all 0.3s cubic-bezier(0.34,1.56,0.64,1); }
        .approve-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(16,185,129,0.3); }
        .reject-btn  { transition: all 0.3s cubic-bezier(0.34,1.56,0.64,1); }
        .reject-btn:hover  { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(239,68,68,0.3); }

        .avatar-ring {
          background: linear-gradient(135deg, #6366f1 0%, #06b6d4 100%);
        }
        .days-pill {
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
        }
        .pulse-dot { animation: pulseDot 2s cubic-bezier(0.4,0,0.6,1) infinite; }
        @keyframes pulseDot { 0%,100%{opacity:1;} 50%{opacity:0.5;} }
      `}</style>

      {/* Header */}
      <div className="header-gradient border-b border-indigo-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
          <div className="flex flex-col sm:flex-row sm:justify-between items-start gap-4">
            <div className="fade-in">
              <h1 className="text-3xl sm:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-cyan-600 bg-clip-text text-transparent mb-2">
                Leave Requests
              </h1>
              <p className="text-gray-600 text-lg">Manage and review employee leave applications</p>
            </div>
            <button className="fade-in-delay-1 button-hover group flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-400 hover:to-indigo-500 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-cyan-500/30">
              <Download size={20} className="group-hover:-translate-y-1 transition-transform" />
              Export Data
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="stat-card gradient-card rounded-2xl p-6 fade-in fade-in-delay-1 border-l-4 border-l-cyan-500">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider mb-2">Total</p>
                <p className="text-4xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-xs text-gray-400 mt-2">All requests</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-indigo-100 rounded-xl flex items-center justify-center">
                <FileText className="text-cyan-600" size={26} />
              </div>
            </div>
          </div>

          <div className="stat-card gradient-card rounded-2xl p-6 fade-in fade-in-delay-2 border-l-4 border-l-violet-500">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider mb-2">Pending</p>
                <p className="text-4xl font-bold text-violet-600">{stats.pending}</p>
                <p className="text-xs text-gray-400 mt-2">Awaiting review</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-violet-100 to-indigo-100 rounded-xl flex items-center justify-center">
                <div className="w-6 h-6 bg-violet-400 rounded-full pulse-dot"></div>
              </div>
            </div>
          </div>

          <div className="stat-card gradient-card rounded-2xl p-6 fade-in fade-in-delay-3 border-l-4 border-l-emerald-500">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider mb-2">Approved</p>
                <p className="text-4xl font-bold text-emerald-600">{stats.approved}</p>
                <p className="text-xs text-gray-400 mt-2">Granted leaves</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="text-emerald-600" size={26} />
              </div>
            </div>
          </div>

          <div className="stat-card gradient-card rounded-2xl p-6 fade-in fade-in-delay-4 border-l-4 border-l-red-500">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider mb-2">Rejected</p>
                <p className="text-4xl font-bold text-red-600">{stats.rejected}</p>
                <p className="text-xs text-gray-400 mt-2">Declined</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-red-100 to-rose-100 rounded-xl flex items-center justify-center">
                <XCircle className="text-red-600" size={26} />
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="gradient-card rounded-2xl p-6 mb-6 fade-in shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-4 top-3.5 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by employee name or reason..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="search-input w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-indigo-300"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="px-4 py-3.5 border border-gray-200 rounded-xl text-gray-900 font-medium bg-gradient-to-r from-slate-50 to-slate-100 focus:outline-none focus:border-indigo-300"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Counter */}
        <div className="mb-4 text-sm text-gray-600 fade-in">
          Showing <span className="font-semibold text-cyan-600">{currentLeaves.length}</span> of{" "}
          <span className="font-semibold text-cyan-600">{filteredLeaves.length}</span> requests
        </div>

        {/* Table */}
        {loading ? (
          <div className="gradient-card rounded-2xl p-16 text-center fade-in shadow-sm">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-cyan-500 mx-auto"></div>
              <p className="text-gray-500 mt-4 font-medium">Loading leave requests...</p>
            </div>
          </div>
        ) : (
          <div className="gradient-card rounded-2xl overflow-hidden fade-in shadow-sm">
            <div className="">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-indigo-50 to-cyan-50 border-b border-indigo-100">
                  <tr>
                    {["Employee", "From", "To", "Days", "Reason", "Status", "Action"].map((h) => (
                      <th key={h} className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentLeaves.length > 0 ? (
                    currentLeaves.map((leave, i) => {
                      const status = leave.status?.toLowerCase();
                      return (
                        <tr key={leave._id || i} className="table-row-hover">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="avatar-ring w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                {leave.user?.name?.[0]?.toUpperCase() || "?"}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900">{leave.user?.name}</p>
                                <p className="text-xs text-gray-400">{leave.user?.designation}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1.5">
                              <Calendar size={14} className="text-cyan-500" />
                              <span className="text-sm text-gray-700 font-medium">{leave.fromDate}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1.5">
                              <Calendar size={14} className="text-indigo-400" />
                              <span className="text-sm text-gray-700 font-medium">{leave.toDate}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="days-pill inline-flex items-center justify-center w-9 h-9 rounded-xl text-white font-bold text-sm shadow-md shadow-indigo-200">
                              {leave.totalDays}
                            </span>
                          </td>
                          <td className="px-6 py-4 max-w-[160px]">
                            <p className="text-sm text-gray-600 truncate">{leave.reason}</p>
                          </td>
                          <td className="px-6 py-4">{getStatusBadge(leave.status)}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                onClick={() => setSelected(leave)}
                                className="text-cyan-600 hover:text-cyan-700 font-semibold hover:underline transition-colors flex items-center gap-1 text-sm"
                              >
                                <Eye size={16} /> View
                              </button>
                              {status === "pending" && (
                                <>
                                  <button
                                    onClick={() => approveLeave(leave._id)}
                                    className="approve-btn bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 shadow-sm"
                                  >
                                    <CheckCircle size={12} /> Approve
                                  </button>
                                  <button
                                    onClick={() => rejectLeave(leave._id)}
                                    className="reject-btn bg-gradient-to-r from-red-500 to-rose-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 shadow-sm"
                                  >
                                    <XCircle size={12} /> Reject
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <Search className="text-gray-300 mb-3" size={48} />
                          <p className="text-gray-600 text-lg font-semibold">No requests found</p>
                          <p className="text-gray-400 text-sm mt-1">
                            {searchTerm ? "Try adjusting your search" : "No leave records available"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between fade-in">
            <div className="text-sm text-gray-600">
              Page <span className="font-semibold text-cyan-600">{currentPage}</span> of{" "}
              <span className="font-semibold text-cyan-600">{totalPages}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gradient-to-r hover:from-cyan-50 hover:to-indigo-50 hover:border-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >Previous</button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (currentPage <= 3) p = i + 1;
                  else if (currentPage >= totalPages - 2) p = totalPages - 4 + i;
                  else p = currentPage - 2 + i;
                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(p)}
                      className={`px-3 py-2 rounded-lg font-medium transition-all ${
                        currentPage === p
                          ? "bg-gradient-to-r from-cyan-500 to-indigo-600 text-white shadow-lg shadow-cyan-500/30"
                          : "border border-gray-200 text-gray-700 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-indigo-50 hover:border-cyan-300"
                      }`}
                    >{p}</button>
                  );
                })}
              </div>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gradient-to-r hover:from-cyan-50 hover:to-indigo-50 hover:border-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {/* View Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4 modal-backdrop">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[80vh] overflow-y-auto border border-indigo-100 modal-content">

            <div className="flex justify-between items-center px-6 py-6 border-b border-indigo-100 sticky top-0 bg-white/95 backdrop-blur-sm">
              <h3 className="text-xl font-bold text-gray-900">Leave Details</h3>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">

              {/* Employee */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-4 tracking-wider">Employee Information</h4>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-indigo-50 to-cyan-50 border border-indigo-100">
                  <div className="avatar-ring w-14 h-14 rounded-full flex items-center justify-center text-white font-extrabold text-xl flex-shrink-0">
                    {selected.user?.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-lg">{selected.user?.name}</p>
                    <p className="text-sm text-gray-500">{selected.user?.designation || "Employee"}</p>
                  </div>
                  <div className="ml-auto">{getStatusBadge(selected.status)}</div>
                </div>
              </div>

              {/* Leave Period */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-4 tracking-wider">Leave Period</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="gradient-card rounded-xl p-4 border-l-4 border-l-cyan-500 text-center">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">From</p>
                    <Calendar size={18} className="text-cyan-500 mx-auto mb-2" />
                    <p className="font-bold text-gray-900 text-sm">{selected.fromDate}</p>
                  </div>
                  <div className="gradient-card rounded-xl p-4 text-center flex flex-col items-center justify-center">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Total Days</p>
                    <p className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-cyan-600 bg-clip-text text-transparent">
                      {selected.totalDays}
                    </p>
                    <p className="text-xs text-gray-400">days</p>
                  </div>
                  <div className="gradient-card rounded-xl p-4 border-l-4 border-l-indigo-500 text-center">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">To</p>
                    <Calendar size={18} className="text-indigo-500 mx-auto mb-2" />
                    <p className="font-bold text-gray-900 text-sm">{selected.toDate}</p>
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-4 tracking-wider">Reason</h4>
                <div className="gradient-card rounded-xl p-4">
                  <p className="text-gray-700 text-sm leading-relaxed">{selected.reason || "No reason provided"}</p>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-4 border-t border-indigo-100">
                <p className="text-xs text-gray-500">
                  Submitted: <span className="font-semibold text-gray-700">{selected.createdAt}</span>
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                {selected.status?.toLowerCase() === "pending" && (
                  <>
                    <button
                      onClick={() => approveLeave(selected._id)}
                      className="approve-btn flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20"
                    >
                      <CheckCircle size={18} /> Approve
                    </button>
                    <button
                      onClick={() => rejectLeave(selected._id)}
                      className="reject-btn flex-1 bg-gradient-to-r from-red-500 to-rose-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-red-500/20"
                    >
                      <XCircle size={18} /> Reject
                    </button>
                  </>
                )}
                <button
                  onClick={() => setSelected(null)}
                  className="flex-1 px-4 py-3 border border-indigo-200 rounded-xl text-gray-700 font-semibold hover:bg-gradient-to-r hover:from-indigo-50 hover:to-cyan-50 hover:border-cyan-300 transition-all"
                >
                  Close
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaves;